
import { Mail, Instagram, Youtube, X, Copyright, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/unified-permissions";
import { useNavigate, Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";

const Footer = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleJoinClick = () => {
    if (!isAuthenticated) {
      navigate('/auth/signup');
    } else {
      navigate('/journal');
    }
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    setIsSubmitting(true);
    
    try {

      
      // Use the email subscription service
      const { subscribeToNewsletter } = await import('@/services/emailSubscriptionService');
      const response = await subscribeToNewsletter(email);
      

      
      if (response.error) {
        console.error('Subscription error:', response.error);
        toast.error(response.error);
        return;
      }

      setEmail("");
      setIsSubscribed(true);
      
      // Check if already subscribed or needs confirmation
      if (response.alreadySubscribed) {
        toast.success("You're already subscribed to our newsletter!");
      } else if (response.needsConfirmation) {
        toast.success("Almost there! Please check your inbox to confirm your email.");
      } else {
        toast.success("Thank you for subscribing!");
      }
      
      // Reset the subscribed state after 5 seconds
      setTimeout(() => {
        setIsSubscribed(false);
      }, 5000);
    } catch (error) {
      console.error("Newsletter subscription error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqs = [{
    question: "Is this really free?",
    answer: "Yup. No credit card. No catch. You can use the core of the app for as long as you want, totally free."
  }, {
    question: "Do I need to be a certain belt level?",
    answer: "Nope. If you're just starting out, we'll help you build smart habits. If you're seasoned, we'll help you refine your edge."
  }, {
    question: "What's in Premium?",
    answer: "That's where your personal game really comes to life — the planner, the vault, the exclusive templates. It's for the folks who are ready to organize their entire Jiu Jitsu lifestyle in one place."
  }];

  return (
    <footer className="bg-white">
      {/* FAQ Section */}
      <div id="faq-section" className="container-padding py-24 border-t border-neutral-200">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          <div className="space-y-8">
            {faqs.map(faq => (
              <div key={faq.question} className="border-b border-neutral-200 pb-6">
                <h3 className="text-xl font-semibold mb-2">{faq.question}</h3>
                <p className="text-neutral-600 text-left">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="container-padding py-24 bg-neutral-100 text-center">
        <h2 className="text-3xl font-bold mb-4">Ready to Train Smarter?</h2>
        <p className="text-neutral-600 mb-8">Start for free. Stick with it. Let your game tell a story.</p>
        <button className="button-primary px-8 py-4 text-lg" onClick={handleJoinClick}>
          {isAuthenticated ? "Go to Journal" : "Join Free Now"}
        </button>
      </div>

      {/* Footer links */}
      <div className="border-t border-neutral-200 bg-white">
        <div className="container-padding py-12 md:py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="flex flex-col justify-center">
              {/* Email Sign-up Box */}
              <form onSubmit={handleSubscribe} className="bg-neutral-50 p-4 rounded-lg border border-neutral-200 shadow-sm">
                <p className="text-sm font-medium text-neutral-700 mb-2">Get TMM news in your inbox</p>
                <div className="flex space-x-2">
                  <div className="relative flex-grow">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 h-4 w-4" />
                    <Input 
                      type="email" 
                      placeholder="Your email" 
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-9 pr-3 py-2 w-full text-sm bg-white" 
                      disabled={isSubmitting || isSubscribed}
                    />
                  </div>
                  <Button 
                    type="submit" 
                    variant="default" 
                    size="sm" 
                    disabled={isSubmitting || isSubscribed}
                    className={`whitespace-nowrap ${isSubscribed ? 'bg-green-600' : 'bg-primary'} hover:bg-primary/90`}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Subscribing</span>
                      </span>
                    ) : isSubscribed ? (
                      <span className="flex items-center">
                        <Check className="mr-1 h-4 w-4" />
                        <span>Subscribed</span>
                      </span>
                    ) : (
                      "Subscribe"
                    )}
                  </Button>
                </div>
                <p className="text-xs text-neutral-500 mt-2">We respect your privacy. Unsubscribe anytime.</p>
              </form>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Features</h4>
              <ul className="space-y-3 text-sm text-neutral-600">
                <li><Link to="/landing#features" className="hover:text-primary transition-colors">Training Journal</Link></li>
                <li><Link to="/landing#features" className="hover:text-primary transition-colors">Technique Library</Link></li>
                <li><Link to="/landing#features" className="hover:text-primary transition-colors">Game Planner</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-3 text-sm text-neutral-600">
                <li><Link to="/about" className="hover:text-primary transition-colors">About</Link></li>
                <li><Link to="/blog" className="hover:text-primary transition-colors">Blog</Link></li>
                <li><Link to="/contact" className="hover:text-primary transition-colors">Contact</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-3 text-sm text-neutral-600">
                <li><Link to="/legal#privacy" className="hover:text-primary transition-colors">Privacy</Link></li>
                <li><Link to="/legal#terms" className="hover:text-primary transition-colors">Terms</Link></li>
                <li><Link to="/legal#cookies" className="hover:text-primary transition-colors">Cookies</Link></li>
                <li><Link to="/legal#eula" className="hover:text-primary transition-colors">EULA</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-neutral-200 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-neutral-600 flex items-center">
              <Copyright size={16} className="mr-1" />
              {new Date().getFullYear()} The Mat Monster. All rights reserved.
            </p>
            <div className="flex gap-6">
              <a href="https://www.instagram.com/thematmonster?igsh=MXcwbG5pOWplMDA1bg%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" className="text-neutral-600 hover:text-primary transition-colors">
                <Instagram size={20} aria-label="Instagram" />
              </a>
              <a href="https://www.youtube.com/@The_Mat_Monster" target="_blank" rel="noopener noreferrer" className="text-neutral-600 hover:text-primary transition-colors">
                <Youtube size={20} aria-label="YouTube" />
              </a>
              <a href="https://x.com/thematmonster1?s=21" target="_blank" rel="noopener noreferrer" className="text-neutral-600 hover:text-primary transition-colors">
                <X size={20} aria-label="X (formerly Twitter)" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
